/* eslint-disable react-hooks/exhaustive-deps */
// src/components/OfficeRegistrationForm.jsx
import React, { useState, useEffect, useCallback, useRef } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useLocation } from "react-router-dom";
import LanguageLink from "../routing/LanguageLink";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";
import api from "../../utils/api";
import ChoosePlanButton from "../../utils/ChoosePlanButton";

const FORM_STORAGE_KEY = "office_registration_form_data";

const OfficeRegistrationForm = ({
  selectedPlan,
  setSelectedPlan,
  openPlansModal,
  plans = [],
}) => {
  const [isVerifying, setIsVerifying] = useState(false);
  const [registrationToken, setRegistrationToken] = useState(null);
  const [emailStatus, setEmailStatus] = useState({
    checking: false,
    available: null,
    message: "",
    reason: null,
  });
  const [hasPendingPayment, setHasPendingPayment] = useState(false);
  const [autoRegistering, setAutoRegistering] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const initialCheckDone = useRef(false);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors, isSubmitting },
  } = useForm();

  const watchedEmail = watch("email");

  useEffect(() => {
    const subscription = watch((value) => {
      const saveData = {
        ...value,
        plan_id: selectedPlan?.id || null,
      };
      localStorage.setItem(FORM_STORAGE_KEY, JSON.stringify(saveData));
    });
    return () => subscription.unsubscribe();
  }, [watch, selectedPlan]);

  useEffect(() => {
    if (initialCheckDone.current) return;

    const saved = localStorage.getItem(FORM_STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);

      Object.keys(parsed).forEach((key) => {
        if (key !== "plan_id") setValue(key, parsed[key]);
      });

      if (parsed.plan_id && plans.length > 0) {
        const plan = plans.find((p) => p.id === parsed.plan_id);
        if (plan) setSelectedPlan(plan);
      }

      if (parsed.email) {
        initialCheckDone.current = true;
        verifyPaymentByEmail(parsed.email);
      }
    }
  }, [setValue, plans, setSelectedPlan]);

  const verifyPaymentByEmail = async (email) => {
    if (!email) return;
    setIsVerifying(true);
    try {
      const response = await api.post("/office-registration/verify-payment-by-email", { email });
      const data = response.data.data;

      if (data.has_pending_registration && data.payment_completed) {
        setRegistrationToken(data.registration_token);
        setHasPendingPayment(true);

        if (data.plan_id && plans.length > 0) {
          const plan = plans.find((p) => p.id === data.plan_id);
          if (plan) setSelectedPlan(plan);
        }

        setIsVerifying(false);
        setAutoRegistering(true);
        autoCompleteRegistration(email);
        return;
      }
    } catch (error) {
    } finally {
      setIsVerifying(false);
    }

    checkEmailAvailability(email);
  };

  const checkEmailAvailability = useCallback(
    async (email) => {
      if (!email || !/\S+@\S+\.\S+/.test(email)) return;

      setEmailStatus({ checking: true, available: null, message: "", reason: null });
      try {
        const response = await api.post("/office-registration/check-email", { email });
        const { available, reason, registration_token } = response.data.data;

        if (reason === "payment_pending_registration") {
          setRegistrationToken(registration_token);
          setHasPendingPayment(true);
          setEmailStatus({
            checking: false,
            available: true,
            message: "Payment completed. You can now complete registration.",
            reason,
          });
          setAutoRegistering(true);
          autoCompleteRegistration(email);
        } else {
          setEmailStatus({
            checking: false,
            available,
            message: available
              ? "Email is available"
              : reason === "user_exists"
                ? "This email is already registered."
                : "Email is already taken",
            reason,
          });
        }
      } catch (error) {
        setEmailStatus({
          checking: false,
          available: false,
          message: "Failed to check email",
          reason: null,
        });
      }
    },
    []
  );

  useEffect(() => {
    if (initialCheckDone.current) return;

    const timeout = setTimeout(() => {
      if (watchedEmail) {
        checkEmailAvailability(watchedEmail);
      }
    }, 600);
    return () => clearTimeout(timeout);
  }, [watchedEmail, checkEmailAvailability]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const urlToken = params.get("token");

    if (urlToken && !registrationToken) {
      setIsVerifying(true);
      verifyPaymentByToken(urlToken);
    }
  }, [location, registrationToken]);

  const verifyPaymentByToken = async (token) => {
    try {
      const response = await api.post("/office-registration/verify-payment", {
        registration_token: token,
      });

      if (response.data.data.payment_completed) {
        setRegistrationToken(token);
        setHasPendingPayment(true);
        setIsVerifying(false);
        Swal.fire({
          icon: "success",
          title: "Payment Successful!",
          text: "You can now complete registration.",
          timer: 2000,
          showConfirmButton: false,
        });
      }
    } catch (error) {
      setIsVerifying(false);
      Swal.fire({
        icon: "error",
        title: "Payment Failed",
        text: "Please try again.",
      });
    }
  };

  // 7. إنشاء جلسة دفع
  const createCheckoutSession = async (email, planId) => {
    try {
      const response = await api.post("/office-registration/checkout", {
        plan_id: planId,
        email,
      });

      const { checkout_url } = response.data.data;
      window.location.href = checkout_url;
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Payment Error",
        text: error.response?.data?.message || "Failed to start payment.",
      });
    }
  };

  // 8. تسجيل تلقائي (حسب الـ API المرسل)
  const autoCompleteRegistration = async (email) => {
    const saved = localStorage.getItem(FORM_STORAGE_KEY);
    if (!saved) return;

    const data = JSON.parse(saved);
    const payload = {
      registration_token: registrationToken,
      name: data.name,
      email: data.email,
      password: data.password,
      password_confirmation: data.password_confirmation,
      phone: data.phone,
      office_name: data.office_name,
      office_address: data.office_address,
      office_city: data.office_city,
      office_state: data.office_state,
      office_zip: data.office_zip,
      office_phone: data.office_phone,
    };

    try {
      const response = await api.post("/auth/register-office", payload);

      Swal.fire({
        icon: "success",
        title: "Office Registered!",
        text: response.data.message || "Please verify your email.",
        confirmButtonText: "OK",
        allowOutsideClick: false,
      }).then(() => {
        localStorage.removeItem(FORM_STORAGE_KEY);
        navigate(`/${i18n.language}/verify-otp`, { state: { email } });
      });
    } catch (error) {
      const message = error.response?.data?.message || "Auto registration failed.";
      Swal.fire({
        icon: "error",
        title: "Error",
        text: message,
      });
      setAutoRegistering(false);
    }
  };

  // 9. إكمال التسجيل يدويًا
  const completeRegistration = async (data) => {
    const payload = {
      registration_token: registrationToken,
      name: data.name,
      email: data.email,
      password: data.password,
      password_confirmation: data.password_confirmation,
      phone: data.phone,
      office_name: data.office_name,
      office_address: data.office_address,
      office_city: data.office_city,
      office_state: data.office_state,
      office_zip: data.office_zip,
      office_phone: data.office_phone,
    };

    try {
      const response = await api.post("/auth/register-office", payload);

      Swal.fire({
        icon: "success",
        title: "Office Registered!",
        text: response.data.message || "Please verify your email.",
        confirmButtonText: "OK",
        allowOutsideClick: false,
      }).then(() => {
        localStorage.removeItem(FORM_STORAGE_KEY);
        navigate(`/${i18n.language}/verify-otp`, { state: { email: data.email } });
      });
    } catch (error) {
      const message = error.response?.data?.message || "Registration failed.";
      Swal.fire({
        icon: "error",
        title: "Error",
        text: message,
      });
    }
  };

  // 10. الإرسال
  const onSubmit = async (data) => {
    if (!selectedPlan) {
      Swal.fire({
        icon: "error",
        title: "Plan Required",
        text: "Please select a plan first.",
      });
      return;
    }

    if (hasPendingPayment && registrationToken) {
      await completeRegistration(data);
      return;
    }

    await createCheckoutSession(data.email, selectedPlan.id);
  };

  // 11. لون البوردر
  const getInputBorder = (fieldName) => {
    if (errors[fieldName]) return "border-red-500";
    if (watch(fieldName) && !errors[fieldName]) return "border-green-500";
    return "border-gray-300";
  };

  return (
    <>
      {/* لودر */}
      {(isVerifying || autoRegistering) && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center">
          <div className="bg-white rounded-xl p-8 shadow-xl text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-lg font-medium text-gray-700">
              {autoRegistering ? "Completing your registration..." : "Verifying your payment..."}
            </p>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Office Name */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">Office Name *</label>
            <input
              {...register("office_name", {
                required: "Office name is required",
                pattern: {
                  value: /^[A-Za-z\s]+$/,
                  message: "Name must contain letters only",
                }
              })}
              className={`w-full px-4 py-3 border-2 rounded-lg focus:ring-2 focus:ring-blue-500 ${getInputBorder("office_name")}`}
              placeholder="Enter office name"
            />
            {errors.office_name && <p className="mt-1 text-sm text-red-600">{errors.office_name.message}</p>}
          </div>

          {/* Owner Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Owner Name *</label>
            <input
              {...register("name", { required: "Owner name is required" })}
              className={`w-full px-4 py-3 border-2 rounded-lg focus:ring-2 focus:ring-blue-500 ${getInputBorder("name")}`}
              placeholder="Your full name"
            />
            {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>}
          </div>

          {/* Phone */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Phone *</label>
            <input
              {...register("phone", { required: "Phone is required" })}
              type="tel"
              className={`w-full px-4 py-3 border-2 rounded-lg focus:ring-2 focus:ring-blue-500 ${getInputBorder("phone")}`}
              placeholder="+1234567890"
            />
            {errors.phone && <p className="mt-1 text-sm text-red-600">{errors.phone.message}</p>}
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Email Address *</label>
            <div className="relative">
              <input
                {...register("email", {
                  required: "Email is required",
                  pattern: { value: /\S+@\S+\.\S+/, message: "Invalid email" },
                })}
                type="email"
                className={`w-full px-4 py-3 border-2 rounded-lg focus:ring-2 focus:ring-blue-500 ${getInputBorder("email")}`}
                placeholder="office@email.com"
              />
              {emailStatus.checking && (
                <div className="absolute right-3 top-3">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                </div>
              )}
              {emailStatus.available !== null && !emailStatus.checking && (
                <div className={`absolute right-3 top-3 text-sm ${emailStatus.available ? "text-green-600" : "text-red-600"}`}>
                  {emailStatus.available ? "Available" : "Taken"}
                </div>
              )}
            </div>
            {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>}
            {emailStatus.message && !errors.email && (
              <p className={`mt-1 text-sm ${emailStatus.reason === "payment_pending_registration" ? "text-green-600" : emailStatus.available ? "text-green-600" : "text-red-600"}`}>
                {emailStatus.message}
              </p>
            )}
          </div>

          {/* Password */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Password *</label>
            <input
              {...register("password", {
                required: "Password is required",
                minLength: { value: 8, message: "Min 8 characters" },
              })}
              type="password"
              className={`w-full px-4 py-3 border-2 rounded-lg focus:ring-2 focus:ring-blue-500 ${getInputBorder("password")}`}
              placeholder="Create password"
            />
            {errors.password && <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>}
          </div>

          {/* Confirm Password */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Confirm Password *</label>
            <input
              {...register("password_confirmation", {
                required: "Please confirm password",
                validate: (value) => value === watch("password") || "Passwords do not match",
              })}
              type="password"
              className={`w-full px-4 py-3 border-2 rounded-lg focus:ring-2 focus:ring-blue-500 ${getInputBorder("password_confirmation")}`}
              placeholder="Re-enter password"
            />
            {errors.password_confirmation && <p className="mt-1 text-sm text-red-600">{errors.password_confirmation.message}</p>}
          </div>

          {/* Office Address */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">Office Address *</label>
            <textarea
              {...register("office_address", { required: "Address is required" })}
              rows={3}
              className={`w-full px-4 py-3 border-2 rounded-lg focus:ring-2 focus:ring-blue-500 ${getInputBorder("office_address")}`}
              placeholder="Full address"
            />
            {errors.office_address && <p className="mt-1 text-sm text-red-600">{errors.office_address.message}</p>}
          </div>

          {/* City */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">City *</label>
            <input
              {...register("office_city", { required: "City is required" })}
              className={`w-full px-4 py-3 border-2 rounded-lg focus:ring-2 focus:ring-blue-500 ${getInputBorder("office_city")}`}
              placeholder="City"
            />
            {errors.office_city && <p className="mt-1 text-sm text-red-600">{errors.office_city.message}</p>}
          </div>

          {/* State */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">State *</label>
            <input
              {...register("office_state", { required: "State is required" })}
              className={`w-full px-4 py-3 border-2 rounded-lg focus:ring-2 focus:ring-blue-500 ${getInputBorder("office_state")}`}
              placeholder="State"
            />
            {errors.office_state && <p className="mt-1 text-sm text-red-600">{errors.office_state.message}</p>}
          </div>

          {/* ZIP */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">ZIP Code *</label>
            <input
              {...register("office_zip", { required: "ZIP is required" })}
              className={`w-full px-4 py-3 border-2 rounded-lg focus:ring-2 focus:ring-blue-500 ${getInputBorder("office_zip")}`}
              placeholder="ZIP"
            />
            {errors.office_zip && <p className="mt-1 text-sm text-red-600">{errors.office_zip.message}</p>}
          </div>

          {/* Office Phone */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Office Phone *</label>
            <input
              {...register("office_phone", { required: "Office phone is required" })}
              type="tel"
              className={`w-full px-4 py-3 border-2 rounded-lg focus:ring-2 focus:ring-blue-500 ${getInputBorder("office_phone")}`}
              placeholder="+1234567890"
            />
            {errors.office_phone && <p className="mt-1 text-sm text-red-600">{errors.office_phone.message}</p>}
          </div>
        </div>

        {/* Choose Plan Button */}
        <ChoosePlanButton
          selectedPlan={selectedPlan}
          onClick={openPlansModal}
          onRemove={() => {
            setSelectedPlan(null);
            localStorage.removeItem(FORM_STORAGE_KEY);
            reset();
            setHasPendingPayment(false);
            setRegistrationToken(null);
            initialCheckDone.current = false;
          }}
          error={!selectedPlan ? "Please select a plan" : ""}
        />

        {/* Terms */}
        <div className="flex items-start space-x-3">
          <input
            {...register("termsAccepted", { required: "You must accept the terms" })}
            type="checkbox"
            className="mt-1 h-4 w-4 text-blue-600 rounded border-2 border-gray-300 focus:ring-blue-500"
          />
          <label className="text-sm text-gray-600">
            I agree to the{" "}
            <LanguageLink to="/terms-of-service" className="text-blue-600 hover:underline">Terms</LanguageLink>{" "}
            and{" "}
            <LanguageLink to="/privacy-policy" className="text-blue-600 hover:underline">Privacy Policy</LanguageLink>
          </label>
        </div>
        {errors.termsAccepted && <p className="text-sm text-red-600">{errors.termsAccepted.message}</p>}

        <button
          type="submit"
          disabled={isSubmitting || isVerifying || autoRegistering || emailStatus.checking}
          className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 disabled:bg-blue-400 transition"
        >
          {autoRegistering
            ? "Completing Registration..."
            : isVerifying
              ? "Verifying Payment..."
              : hasPendingPayment && registrationToken
                ? "Complete Registration"
                : "Proceed to Payment"}
        </button>
      </form>
    </>
  );
};

export default OfficeRegistrationForm;